import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { X, Download, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/api";
import { toast } from "sonner";

export const EbookModal = ({ ebook, open, onClose }) => {
  const [stage, setStage] = useState("details");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);

  const reset = () => {
    setStage("details");
    setEmail("");
    setOrder(null);
    onClose();
  };

  const purchase = async (e) => {
    e.preventDefault();
    setStage("processing");
    try {
      const { data } = await api.post("/orders", { ebook_id: ebook.id, email });
      setOrder(data);
      setStage("success");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStage("checkout");
    }
  };

  if (!ebook) return null;
  const downloadUrl = order
    ? `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/download?token=${order.download_token}`
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && reset()}>
      <DialogContent
        className="bg-[#121114] border border-white/20 text-foreground rounded-none max-w-3xl p-0 overflow-hidden [&>button]:hidden"
        data-testid="ebook-modal"
      >
        <button
          onClick={reset}
          className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center border border-white/20 bg-black/60 backdrop-blur-md hover:bg-primary hover:border-primary transition-colors duration-300"
          data-testid="ebook-modal-close-button"
        >
          <X size={16} />
        </button>
        <div className="grid md:grid-cols-2">
          <div className="relative spotlight min-h-[280px]">
            <img src={ebook.cover_url} alt={ebook.title} className="absolute inset-0 w-full h-full object-cover" data-testid="ebook-modal-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121114] via-transparent to-transparent md:bg-gradient-to-r" />
          </div>
          <div className="p-8 md:p-10 flex flex-col justify-center min-h-[380px]">
            <AnimatePresence mode="wait">
              {stage === "details" && (
                <motion.div key="details" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
                  <p className="text-[10px] font-semibold tracking-[0.35em] uppercase text-primary mb-3">{ebook.category}</p>
                  <h3 className="font-display uppercase text-3xl md:text-4xl leading-none" data-testid="ebook-modal-title">{ebook.title}</h3>
                  <p className="font-editorial italic text-lg text-foreground/70 mt-2">{ebook.subtitle}</p>
                  <p className="text-sm leading-relaxed text-foreground/60 mt-5">{ebook.description}</p>
                  <div className="flex gap-6 mt-5 text-[11px] uppercase tracking-widest text-foreground/40">
                    <span>{ebook.pages} pages</span>
                    <span>{ebook.year}</span>
                    <span>PDF + EPUB</span>
                  </div>
                  <div className="flex items-center justify-between mt-8">
                    <span className="font-display text-3xl text-foreground" data-testid="ebook-modal-price">${ebook.price.toFixed(2)}</span>
                    <button
                      onClick={() => setStage("checkout")}
                      className="bg-primary text-white font-display uppercase tracking-wider text-sm px-8 py-3 hover:bg-[#a00d26] active:scale-95 transition-[background-color,transform] duration-200"
                      data-testid="buy-ebook-button"
                    >
                      Buy Now
                    </button>
                  </div>
                </motion.div>
              )}

              {stage === "checkout" && (
                <motion.form key="checkout" onSubmit={purchase} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
                  <p className="text-[10px] font-semibold tracking-[0.35em] uppercase text-primary mb-3">Mock Checkout — no real payment</p>
                  <h3 className="font-display uppercase text-2xl">Where do we send it?</h3>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@garage.com"
                    className="mt-6 w-full bg-black/40 border border-white/20 px-4 py-3 text-sm outline-none focus:border-primary transition-colors duration-300 placeholder:text-foreground/30"
                    data-testid="checkout-email-input"
                  />
                  <div className="flex items-center justify-between mt-8">
                    <span className="font-display text-2xl">${ebook.price.toFixed(2)}</span>
                    <button type="submit" className="bg-primary text-white font-display uppercase tracking-wider text-sm px-8 py-3 hover:bg-[#a00d26] active:scale-95 transition-[background-color,transform] duration-200" data-testid="checkout-pay-button">
                      Pay & Download
                    </button>
                  </div>
                </motion.form>
              )}

              {stage === "processing" && (
                <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 py-12" data-testid="checkout-processing">
                  <Loader2 className="animate-spin text-primary" size={32} />
                  <p className="font-editorial italic text-lg text-foreground/70">Firing up the presses…</p>
                </motion.div>
              )}

              {stage === "success" && (
                <motion.div key="success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-center">
                  <p className="font-display uppercase text-3xl text-primary">It's yours.</p>
                  <p className="text-sm text-foreground/60 mt-3">
                    Order confirmed for <span className="text-foreground">{order?.buyer_email}</span>
                  </p>
                  <a
                    href={downloadUrl}
                    className="mt-8 inline-flex items-center gap-3 bg-primary text-white font-display uppercase tracking-wider text-sm px-8 py-3 hover:bg-[#a00d26] active:scale-95 transition-[background-color,transform] duration-200"
                    data-testid="download-ebook-button"
                  >
                    <Download size={16} /> Download Your Copy
                  </a>
                  <p className="mt-4 text-[11px] text-foreground/40">Sample file — full designed PDF ships in production.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
